/**
 * SchoolPerformanceSection – academic outcomes across a school.
 *
 * Shared by the school admin, principal and platform-admin screens, which
 * already load the school's classes, students, teachers and scores; this
 * derives everything from those with lib/analytics/metrics.ts.
 *
 * Deliberately aggregate. Individual quiz detail is one click away (class →
 * lesson → student), and teachers appear only as operational activity —
 * lessons run, students reached — never ranked by their students' marks.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  DEFAULT_PASS_THRESHOLD,
  bandCounts,
  byTime,
  dailyTrend,
  formatPercent,
  groupPerformance,
  inRange,
  isValid,
  lessonTitle,
  normaliseScore,
  previousRange,
  scoreTone,
  studentPerformance,
  summarise,
  type RawScore,
  type Result,
} from '../../lib/analytics/metrics';
import { getPassThreshold, setPassThreshold as savePassThreshold } from '../../services/performanceService';
import { MetricCard, MetricGrid } from './MetricCard';
import { ComparisonChart, DistributionChart, TrendChart } from './charts';
import { DataTable, type Column } from './DataTable';
import { DateRangeFilter, makeRange, type RangeValue } from './DateRangeFilter';
import { AnalyticsEmptyState, AnalyticsErrorState, AnalyticsNote, AnalyticsPanel, ChartSkeleton, TableSkeleton } from './AnalyticsPanel';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

interface ClassLike {
  id: string;
  class_name?: string;
  teacher_ids?: string[];
  class_teacher_id?: string | null;
}
interface PersonLike {
  uid?: string;
  id?: string;
  name?: string;
  displayName?: string;
  display_name?: string;
  email?: string;
  class_ids?: string[];
  managed_class_ids?: string[];
}

type CompareMetric = 'average' | 'completion' | 'accuracy' | 'passRate';
const COMPARE_LABELS: Record<CompareMetric, string> = {
  average: 'Average score',
  completion: 'Completion',
  accuracy: 'Accuracy',
  passRate: 'Pass rate',
};

const personId = (p: PersonLike) => p.uid || p.id || '';
const personName = (p: PersonLike | undefined) => p?.name || p?.displayName || p?.display_name || p?.email || 'Teacher';
const delta = (current: number | null, previous: number | null | undefined) =>
  current !== null && previous !== null && previous !== undefined ? current - previous : null;

interface ClassActivity {
  id: string;
  name: string;
  teachers: string;
  enrolled: number;
  reached: number;
  sessions: number;
  summary: ReturnType<typeof summarise>;
}

interface SessionActivity {
  sessionId: string;
  classId: string | null;
  lesson: string;
  date: number | null;
  submissions: number;
  average: number | null;
}

export function SchoolPerformanceSection({
  schoolId,
  classes,
  students,
  teachers = [],
  scores,
  loading,
  error,
  canEditThreshold,
  title = 'School academic performance',
}: {
  schoolId: string;
  classes: ClassLike[];
  students: PersonLike[];
  teachers?: PersonLike[];
  scores: unknown[];
  loading: boolean;
  error?: string | null;
  canEditThreshold: boolean;
  title?: string;
}) {
  const navigate = useNavigate();
  const [range, setRange] = useState<RangeValue>(() => makeRange('year'));
  const [metric, setMetric] = useState<CompareMetric>('average');
  const [passThreshold, setPassThresholdState] = useState(DEFAULT_PASS_THRESHOLD);

  useEffect(() => {
    let cancelled = false;
    getPassThreshold(schoolId).then((t) => !cancelled && setPassThresholdState(t));
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  const results = useMemo(() => (scores as RawScore[]).map((s) => normaliseScore(s)), [scores]);
  const inPeriod = useMemo(() => results.filter((r) => inRange(r, range.range)), [results, range]);
  const prevRange = useMemo(() => previousRange(range.range), [range]);
  const inPrevious = useMemo(() => (prevRange ? results.filter((r) => inRange(r, prevRange)) : []), [results, prevRange]);

  const summary = useMemo(() => summarise(inPeriod, passThreshold), [inPeriod, passThreshold]);
  const previous = useMemo(
    () => (inPrevious.some(isValid) ? summarise(inPrevious, passThreshold) : null),
    [inPrevious, passThreshold]
  );
  const perStudent = useMemo(() => studentPerformance(inPeriod, passThreshold), [inPeriod, passThreshold]);
  const activeStudents = perStudent.filter((p) => p.average !== null).length;
  const sessionsInPeriod = useMemo(() => new Set(inPeriod.map((r) => r.sessionId).filter(Boolean)).size, [inPeriod]);

  const trend = useMemo(
    () => dailyTrend(inPeriod).map((p) => ({ x: p.date, value: p.average, note: `${p.count} submission${p.count === 1 ? '' : 's'}` })),
    [inPeriod]
  );
  const bands = useMemo(() => bandCounts(perStudent.map((p) => p.average).filter((a): a is number => a !== null)), [perStudent]);

  const teacherById = useMemo(() => new Map(teachers.map((t) => [personId(t), t])), [teachers]);
  const classActivity = useMemo<ClassActivity[]>(() => {
    const byClass = new Map(
      groupPerformance(inPeriod, (r) => r.classId, () => '', { passThreshold }).map((g) => [g.key, g])
    );
    return classes.map((c) => {
      const list = inPeriod.filter((r) => r.classId === c.id);
      const teacherIds = [...new Set([c.class_teacher_id, ...(c.teacher_ids ?? [])].filter((t): t is string => !!t))];
      return {
        id: c.id,
        name: c.class_name || 'Class',
        teachers: teacherIds.map((id) => personName(teacherById.get(id))).join(', ') || '—',
        enrolled: students.filter((s) => s.class_ids?.includes(c.id)).length,
        reached: new Set(list.filter(isValid).map((r) => r.studentId)).size,
        sessions: new Set(list.map((r) => r.sessionId).filter(Boolean)).size,
        summary: byClass.get(c.id)?.summary ?? summarise([], passThreshold),
      };
    });
  }, [classes, inPeriod, students, teacherById, passThreshold]);

  const comparison = useMemo(
    () =>
      classActivity
        .filter((c) => c.summary.submissions > 0)
        .map((c) => ({
          key: c.id,
          label: c.name,
          value: c.summary[metric],
          note: `${c.summary.validSubmissions} submissions · ${c.reached} students`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
    [classActivity, metric]
  );

  const recentSessions = useMemo<SessionActivity[]>(() => {
    const bySession = new Map<string, Result[]>();
    for (const r of inPeriod) {
      if (!r.sessionId) continue;
      if (!bySession.has(r.sessionId)) bySession.set(r.sessionId, []);
      bySession.get(r.sessionId)!.push(r);
    }
    return [...bySession.entries()]
      .map(([sessionId, list]) => {
        const sorted = [...list].sort(byTime);
        const s = summarise(list, passThreshold);
        return {
          sessionId,
          classId: sorted[0].classId,
          lesson: [...new Set(sorted.map(lessonTitle))].join(', '),
          date: sorted[sorted.length - 1].completedAt,
          submissions: s.validSubmissions,
          average: s.average,
        };
      })
      .sort((a, b) => (b.date ?? 0) - (a.date ?? 0))
      .slice(0, 8);
  }, [inPeriod, passThreshold]);

  const className = (id: string | null) => classes.find((c) => c.id === id)?.class_name || 'Class';

  const activityColumns: Column<ClassActivity>[] = [
    {
      key: 'class',
      header: 'Class',
      sortValue: (c) => c.name,
      cell: (c) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{c.name}</p>
          <p className="truncate text-xs text-muted-foreground">{c.teachers}</p>
        </div>
      ),
    },
    { key: 'sessions', header: 'Live sessions', align: 'right', sortValue: (c) => c.sessions, cell: (c) => c.sessions },
    { key: 'reached', header: 'Students reached', align: 'right', sortValue: (c) => c.reached, cell: (c) => `${c.reached} / ${Math.max(c.enrolled, c.reached)}` },
    { key: 'completion', header: 'Completion', align: 'right', sortValue: (c) => c.summary.completion, cell: (c) => formatPercent(c.summary.completion) },
    {
      key: 'average',
      header: 'Average',
      align: 'right',
      sortValue: (c) => c.summary.average,
      cell: (c) => <span className={scoreTone(c.summary.average, passThreshold)}>{formatPercent(c.summary.average)}</span>,
    },
    {
      key: 'view',
      header: '',
      hideOnMobile: true,
      cell: (c) => (
        <Link to={`/analytics/classes/${c.id}`} onClick={(e) => e.stopPropagation()} className="text-xs font-medium text-primary hover:underline">
          Open
        </Link>
      ),
    },
  ];

  if (error) return <AnalyticsErrorState message={error} />;
  const noData = !loading && results.length === 0;

  return (
    <section className="space-y-5" aria-label={title}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-foreground">{title}</h2>
        <div className="flex flex-wrap items-center gap-2">
          {canEditThreshold && <PassMarkEditor schoolId={schoolId} value={passThreshold} onSaved={setPassThresholdState} />}
          {!noData && <DateRangeFilter value={range} onChange={setRange} />}
        </div>
      </div>

      <MetricGrid columns={4}>
        <MetricCard loading={loading} label="Total students" value={students.length} />
        <MetricCard loading={loading} label="Active students" value={activeStudents} detail="With a quiz result in period" />
        <MetricCard loading={loading} label="Teachers" value={teachers.length || '—'} />
        <MetricCard loading={loading} label="Classes" value={classes.length} />
        <MetricCard
          loading={loading}
          label="Lessons conducted"
          value={sessionsInPeriod}
          detail="Live sessions with results"
          definition="Live class sessions in this period that produced at least one quiz result."
        />
        <MetricCard
          loading={loading}
          label="Average score"
          value={formatPercent(summary.average)}
          detail={`${summary.validSubmissions} submissions`}
          deltaPp={delta(summary.average, previous?.average)}
          definition="Mean percentage across every quiz submission in the school, each weighted equally."
        />
        <MetricCard
          loading={loading}
          label="Completion"
          value={formatPercent(summary.completion)}
          detail="Questions answered"
          deltaPp={delta(summary.completion, previous?.completion)}
        />
        <MetricCard
          loading={loading}
          label="Pass rate"
          value={formatPercent(summary.passRate)}
          detail={`At or above ${passThreshold}%`}
          deltaPp={delta(summary.passRate, previous?.passRate)}
        />
      </MetricGrid>

      {noData ? (
        <AnalyticsPanel title="No performance data yet">
          <AnalyticsEmptyState description="School analytics will appear after students complete a lesson quiz." />
        </AnalyticsPanel>
      ) : (
        <>
          <div className="grid gap-5 lg:grid-cols-5">
            <AnalyticsPanel className="lg:col-span-3" title="School average over time" description="Mean of every submission, by day">
              {loading ? (
                <ChartSkeleton />
              ) : trend.length < 2 ? (
                <AnalyticsEmptyState compact title="Not enough days yet" description="The trend appears once quizzes are taken on two or more days in this period." />
              ) : (
                <TrendChart data={trend} passThreshold={passThreshold} valueLabel="School average" />
              )}
            </AnalyticsPanel>
            <AnalyticsPanel className="lg:col-span-2" title="Performance distribution" description="Students by their average in this period">
              {loading ? <ChartSkeleton /> : activeStudents === 0 ? <AnalyticsEmptyState compact description="No results in this period." /> : <DistributionChart bands={bands} />}
            </AnalyticsPanel>
          </div>

          <AnalyticsPanel
            title="Class comparison"
            description="Open a class to see its lessons and students"
            action={
              <div className="inline-flex flex-wrap rounded-md border border-border p-0.5 text-xs" role="group" aria-label="Metric">
                {(Object.keys(COMPARE_LABELS) as CompareMetric[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    aria-pressed={metric === m}
                    onClick={() => setMetric(m)}
                    className={`rounded px-2.5 py-1 ${metric === m ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {COMPARE_LABELS[m]}
                  </button>
                ))}
              </div>
            }
          >
            {loading ? (
              <ChartSkeleton />
            ) : comparison.filter((c) => c.value !== null).length === 0 ? (
              <AnalyticsEmptyState compact description="No class has results in this period." />
            ) : (
              <ComparisonChart data={comparison} valueLabel={COMPARE_LABELS[metric]} onSelect={(id) => navigate(`/analytics/classes/${id}`)} />
            )}
          </AnalyticsPanel>

          <AnalyticsPanel title="Class and teacher activity" description="What each class has done this period. Activity, not a ranking.">
            {loading ? (
              <TableSkeleton />
            ) : (
              <DataTable
                rows={classActivity}
                columns={activityColumns}
                rowKey={(c) => c.id}
                initialSort={{ key: 'class', direction: 'asc' }}
                searchPlaceholder="Search classes or teachers"
                searchText={(c) => `${c.name} ${c.teachers}`}
                onRowClick={(c) => navigate(`/analytics/classes/${c.id}`)}
                pageSize={15}
              />
            )}
          </AnalyticsPanel>

          <AnalyticsPanel title="Recent learning activity" description="Latest live class sessions with results">
            {recentSessions.length === 0 ? (
              <AnalyticsEmptyState compact title="No live sessions in this period" description="Sessions appear here once a class completes a lesson quiz together." />
            ) : (
              <ul className="divide-y divide-border">
                {recentSessions.map((s) => (
                  <li key={s.sessionId}>
                    <Link to={`/class-session/${s.sessionId}/results`} className="flex items-center gap-3 py-2.5 text-sm hover:text-primary">
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-foreground">{s.lesson}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {[className(s.classId), s.date ? new Date(s.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {s.submissions} submitted · <span className={`font-semibold ${scoreTone(s.average, passThreshold)}`}>{formatPercent(s.average)}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <AnalyticsNote>Averages weight every submission equally, so a large class counts for more than a small one.</AnalyticsNote>
          </AnalyticsPanel>
        </>
      )}
    </section>
  );
}

function PassMarkEditor({ schoolId, value, onSaved }: { schoolId: string; value: number; onSaved: (v: number) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(String(value)), [value]);

  if (!editing) {
    return (
      <button type="button" onClick={() => setEditing(true)} className="h-9 rounded-md border border-input px-3 text-sm text-muted-foreground hover:text-foreground">
        Pass mark: <span className="font-medium text-foreground">{value}%</span>
      </button>
    );
  }

  const save = async () => {
    const n = Number(draft);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      toast.error('Enter a pass mark between 0 and 100.');
      return;
    }
    setSaving(true);
    try {
      await savePassThreshold(schoolId, n);
      onSaved(Math.round(n));
      setEditing(false);
      toast.success('Pass mark updated');
    } catch (err) {
      console.error('[PassMarkEditor] save failed:', err);
      toast.error('The pass mark could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <label htmlFor="pass-mark" className="text-sm text-muted-foreground">
        Pass mark
      </label>
      <Input id="pass-mark" type="number" min={0} max={100} value={draft} onChange={(e) => setDraft(e.target.value)} className="h-9 w-20 text-sm" />
      <span className="text-sm text-muted-foreground">%</span>
      <Button size="sm" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
        Cancel
      </Button>
    </div>
  );
}
